import { gql, useQuery } from '@apollo/client';
import { useContext, useMemo, useState } from 'react';
import useStoreAndLanguageParams from '../../core/hooks/useStoreAndLanguageParams';
import type { PageComponent } from '../../content/types/types';
import Storyblok from '../../content/storyblok/storyblok';
import PreorderModeContext from '../../preorder/PreorderModeContext';
import useContentClient from '../../content/hooks/useContentClient';
import useEffectWithoutMount from '../../core/util/useEffectWithoutMount';
import { STORYBLOK_ERROR_MESSAGE } from '../../content/constants';

const pageQuery = gql`
    query postQuery($slug: ID!) {
        PageItem(id: $slug) {
            content {
                body
                component
                title
                meta_fields
            }
            name
            id
            lang
        }
    }
`;

const useHomePage = () => {
    const { store, language } = useStoreAndLanguageParams();

    const { selectedPreorderSeason } = useContext(PreorderModeContext);
    const preorderPageSlug = useMemo(
        () =>
            selectedPreorderSeason
                ? `preorder-pages/${selectedPreorderSeason.cmsRootFolder}`
                : '',
        [selectedPreorderSeason]
    );

    const [pageName, setPageName] = useState(preorderPageSlug);
    const client = useContentClient();

    useEffectWithoutMount(() => {
        setPageName(preorderPageSlug);
    }, [preorderPageSlug]);

    const { data, loading, error } = useQuery<{
        PageItem?: {
            content: PageComponent;
        };
    }>(pageQuery, {
        variables: {
            slug: Storyblok.formatSlug(store, language, pageName),
        },
        client,
        onCompleted: page => {
            if (
                !page.PageItem &&
                selectedPreorderSeason &&
                pageName === preorderPageSlug
            ) {
                setPageName('');
            }
        },
        fetchPolicy: 'cache-and-network',
        nextFetchPolicy: 'cache-only',
    });

    if (error) {
        throw new Error(STORYBLOK_ERROR_MESSAGE, { cause: error });
    }

    return {
        data: data?.PageItem?.content,
        loading,
    };
};

export default useHomePage;
